import { differenceInDays } from 'date-fns'
import { JobStatus } from './dtos/job'

/**
 * The result of the deadline display computation.
 */
export interface DeadlineDisplay {
  proximity: DeadlineProximity
  friendlyText: string
}

/**
 * The props we need to compute the deadline display.
 */
export interface DeadlineDisplayInput {
  status: JobStatus
  dateDeadline?: Date | null
}

/**
 * How close we are to the deadline, in relative terms.
 */
export const enum DeadlineProximity {
  NotApplicable = 'NotApplicable',
  PrettyFar = 'PrettyFar',
  Far = 'Far',
  Close = 'Close',
}

/**
 * Computes the deadline display properties.
 */
export function computeDeadlineDisplay(
  input: DeadlineDisplayInput,
  now = new Date(),
): DeadlineDisplay {
  const daysUntilNow = input.dateDeadline
    ? differenceInDays(input.dateDeadline, now)
    : null
  if (daysUntilNow === null || input.status === 'UNDER_CONSTRUCTION') {
    return { proximity: DeadlineProximity.NotApplicable, friendlyText: 'N/A' }
  }

  const dueIn = `Due in ${daysUntilNow} days`
  // The trick is to put the most specific condition first
  // in the cases.
  switch (true) {
    case daysUntilNow === 1:
      return {
        proximity: DeadlineProximity.Close,
        friendlyText: 'Due tomorrow',
      }
    case daysUntilNow === 0:
      return {
        proximity: DeadlineProximity.Close,
        friendlyText: 'Due today',
      }
    case daysUntilNow === -1:
      return {
        proximity: DeadlineProximity.Close,
        friendlyText: 'Due yesterday',
      }
    case daysUntilNow < -1:
      return {
        proximity: DeadlineProximity.Close,
        friendlyText: `Due ${Math.abs(daysUntilNow)} days ago`,
      }
    case daysUntilNow < 7:
      return {
        proximity: DeadlineProximity.Close,
        friendlyText: dueIn,
      }
    case daysUntilNow < 30:
      return {
        proximity: DeadlineProximity.Far,
        friendlyText: dueIn,
      }
    default:
      return { proximity: DeadlineProximity.PrettyFar, friendlyText: dueIn }
  }
}
